(() => {
  if (!window.gsap || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const root = document.documentElement;

  // Wrap each word in .th-w > .th-wi. Brand marks stay intact and animate as one unit.
  function splitWords(el) {
    if (!el) return [];
    if (!el.dataset.thSplit) {
      el.dataset.thSplit = '1';
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(node => {
        if (node.parentElement.closest('.brand-inline')) return;
        const frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach(part => {
          if (!part) return;
          if (/^\s+$/.test(part)) return frag.append(part);
          const outer = document.createElement('span');
          const inner = document.createElement('span');
          outer.className = 'th-w';
          inner.className = 'th-wi';
          inner.textContent = part;
          outer.append(inner);
          frag.append(outer);
        });
        node.replaceWith(frag);
      });
      el.querySelectorAll('.brand-inline').forEach(b => b.classList.add('th-wi'));
    }
    return [...el.querySelectorAll('.th-wi')];
  }

  // Halftone wordmark: fine ink dots that settle into TALENT® on scroll and ease aside under the cursor.
  function initMark(el) {
    const canvas = el.querySelector('canvas');
    const ctx = canvas.getContext('2d');
    const word = (el.dataset.word || 'Talent').toUpperCase();
    const onColor = getComputedStyle(el).getPropertyValue('--th-on').trim();
    const font = '"Tenor Sans", sans-serif';
    // Canvas text doesn't trigger webfont loading, so wait for Tenor Sans explicitly.
    const fontReady = document.fonts ? document.fonts.load('400 40px "Tenor Sans"').catch(() => {}) : Promise.resolve();
    const state = {p: reduce ? 1.2 : 0};
    const mouse = {x: -1e4, y: -1e4};
    let dots = [], W = 0, H = 0, dpr = 1, gap = 10, running = false, reg = null;

    function build() {
      dpr = Math.min(devicePixelRatio || 1, 2);
      W = el.clientWidth;
      H = el.clientHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + 'px';
      canvas.style.height = H + 'px';
      gap = Math.max(6, Math.round(W / 170));
      const cols = Math.floor(W / gap), rows = Math.floor(H / gap);
      const padX = (W - cols * gap) / 2 + gap / 2, padY = (H - rows * gap) / 2 + gap / 2;

      // Rasterise the word at grid resolution; each pixel becomes one dot.
      const raster = document.createElement('canvas');
      raster.width = cols;
      raster.height = rows;
      const r = raster.getContext('2d', {willReadFrequently: true});
      // Wide-tracked TALENT with a small ® at the cap line, matching the brand lockup.
      let size = rows * 0.5;
      const tracking = 0.3, regScale = 0.2;
      const measure = () => {
        r.font = `400 ${size}px ${font}`;
        if ('letterSpacing' in r) r.letterSpacing = `${size * tracking}px`;
        const wordW = r.measureText(word).width; // includes one trailing letter-space: the gap before ®
        r.font = `400 ${size * regScale}px ${font}`;
        if ('letterSpacing' in r) r.letterSpacing = '0px';
        return {wordW, total: wordW + r.measureText('®').width};
      };
      let m = measure();
      const fit = (cols * 0.84) / m.total;
      if (fit < 1) { size *= fit; m = measure(); }
      const x0 = (cols - m.total) / 2, cy = rows / 2;
      r.textAlign = 'left';
      r.textBaseline = 'middle';
      r.font = `400 ${size}px ${font}`;
      if ('letterSpacing' in r) r.letterSpacing = `${size * tracking}px`;
      r.fillText(word, x0, cy);
      r.lineWidth = Math.max(1, size * 0.03); // thickens strokes that are thinner than one dot
      r.strokeText(word, x0, cy);
      // ® is too small to read as dots, so it's drawn as crisp text on the main canvas (see draw),
      // its top aligned with the top edge of the T's first row of dots.
      const capTop = Math.floor(cy - r.measureText('T').actualBoundingBoxAscent - r.lineWidth / 2);
      const regSize = size * regScale * gap;
      ctx.font = `400 ${regSize}px ${font}`;
      const regAscent = ctx.measureText('®').actualBoundingBoxAscent;
      reg = {
        x: padX + (x0 + m.wordW - size * tracking * 0.55 - 0.5) * gap,
        y: padY + capTop * gap - gap * 0.35 + regAscent, // alphabetic baseline
        size: regSize,
        fade: reg ? reg.fade : 1
      };
      const data = r.getImageData(0, 0, cols, rows).data;

      dots = [];
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          dots.push({
            x: padX + x * gap,
            y: padY + y * gap,
            lit: data[(y * cols + x) * 4 + 3] > 100,
            delay: Math.random() * 0.9, // soft dissolve rather than a left-to-right sweep
            paper: (x + y) % 2 === 0, // sparse background dots, like a light halftone screen
            ox: 0, oy: 0
          });
        }
      }
      if (!running) draw(performance.now());
    }

    function draw(now) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      const R = gap * 11;
      ctx.fillStyle = onColor;
      for (const d of dots) {
        let tx = 0, ty = 0;
        const dx = d.x - mouse.x, dy = d.y - mouse.y, dist = Math.hypot(dx, dy) || 1;
        if (dist < R) {
          const f = 1 - dist / R;
          tx = (dx / dist) * f * gap * 1.1;
          ty = (dy / dist) * f * gap * 1.1;
        }
        d.ox += (tx - d.ox) * 0.08;
        d.oy += (ty - d.oy) * 0.08;

        const lit = d.lit ? Math.min(1, Math.max(0, (state.p - d.delay) / 0.3)) : 0;
        if (!lit && !d.paper) continue;
        ctx.globalAlpha = lit ? 0.08 + lit * 0.62 : 0.07;
        ctx.beginPath();
        ctx.arc(d.x + d.ox, d.y + d.oy, gap * (0.12 + lit * 0.18), 0, 6.2832);
        ctx.fill();
      }
      if (reg) {
        // Fades in as the last dots settle, and fades away as the cursor approaches.
        const near = Math.hypot(reg.x + reg.size / 2 - mouse.x, reg.y - reg.size / 2 - mouse.y);
        reg.fade += (Math.min(1, Math.max(0, (near - R * 0.35) / (R * 0.65))) - reg.fade) * 0.14;
        ctx.globalAlpha = 0.55 * reg.fade * Math.min(1, Math.max(0, (state.p - 0.9) / 0.3));
        ctx.fillStyle = onColor;
        ctx.font = `400 ${reg.size}px ${font}`;
        ctx.textBaseline = 'alphabetic';
        ctx.fillText('®', reg.x, reg.y);
      }
      if (running) requestAnimationFrame(draw);
    }

    fontReady.then(() => {
      build();
      new ResizeObserver(() => requestAnimationFrame(build)).observe(el);
    });
    if (reduce) return;

    gsap.to(state, {
      p: 1.2,
      ease: 'none',
      scrollTrigger: {trigger: el, start: 'top 90%', end: 'center 60%', scrub: 1}
    });
    new IntersectionObserver(([entry]) => {
      const was = running;
      running = entry.isIntersecting;
      if (running && !was) requestAnimationFrame(draw);
    }).observe(el);
    el.addEventListener('pointermove', e => {
      const r = el.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    });
    el.addEventListener('pointerleave', () => { mouse.x = mouse.y = -1e4; });
  }

  const mark = document.querySelector('[data-th-mark]');
  if (mark) initMark(mark);

  // Hero scene: the sunlit sill (still now, video once one exists) replaces the plain lineup photo.
  // Built before the reduced-motion exit so everyone gets the new image; only its motion is optional.
  const scene = (() => {
    const visual = document.querySelector('.hero .hero-visual');
    const src = window.TalentHome || {};
    if (!visual || !src.still) return null;
    const el = document.createElement('div');
    el.className = 'th-scene';
    el.setAttribute('aria-hidden', 'true');
    let media;
    if (src.video && !reduce) {
      media = document.createElement('video');
      media.muted = true;
      media.setAttribute('muted', '');
      Object.assign(media, {loop: true, autoplay: true, playsInline: true, poster: src.still, src: src.video});
    } else {
      media = document.createElement('img');
      media.src = src.still;
      media.alt = '';
    }
    const sun = document.createElement('div');
    sun.className = 'th-sun';
    el.append(media, sun);
    visual.prepend(el);
    visual.classList.add('th-has-scene');
    return {el, media, sun};
  })();

  if (reduce) return;

  root.classList.add('th-motion');
  const grain = document.createElement('div');
  grain.className = 'th-grain';
  document.body.append(grain);

  // Hero: the sill scene opens like a shop door, sunlight breathes across it, the headline rises in,
  // window light follows the cursor and the scene drifts against it.
  const hero = document.querySelector('.hero');
  if (hero) {
    const tl = gsap.timeline({delay: 0.1});
    if (scene) {
      gsap.set(scene.media, {scale: 1.04});
      tl.fromTo(scene.el, {clipPath: 'inset(0% 50% 0% 50%)'},
        {clipPath: 'inset(0% 0% 0% 0%)', duration: 1.9, ease: 'expo.inOut'}, 0)
        .from(scene.media, {scale: 1.3, duration: 2.6, ease: 'expo.out'}, 0.25);
      // Passing clouds: the warm light slowly swells and fades.
      gsap.fromTo(scene.sun, {opacity: 0.35}, {opacity: 1, duration: 5.5, ease: 'sine.inOut', yoyo: true, repeat: -1});
      gsap.to(scene.media, {yPercent: 1.5, ease: 'none',
        scrollTrigger: {trigger: hero, start: 'top top', end: 'bottom top', scrub: true}});
    }
    tl.from(splitWords(hero.querySelector('h1')), {yPercent: 115, duration: 1.2, ease: 'expo.out', stagger: 0.09}, scene ? 0.7 : 0)
      .from(hero.querySelectorAll('.eyebrow, .hero-intro > span, .hero-bottom, .ewg-badge'),
        {autoAlpha: 0, y: 14, duration: 0.9, ease: 'power2.out', stagger: 0.08}, '-=0.8');

    const glow = document.createElement('div');
    glow.className = 'th-glow';
    hero.append(glow);
    const light = {x: hero.clientWidth * 0.7, y: hero.clientHeight * 0.4};
    const paint = () => {
      glow.style.setProperty('--x', light.x + 'px');
      glow.style.setProperty('--y', light.y + 'px');
    };
    paint();

    if (fine) {
      const lx = gsap.quickTo(light, 'x', {duration: 1.2, ease: 'power3.out', onUpdate: paint});
      const ly = gsap.quickTo(light, 'y', {duration: 1.2, ease: 'power3.out', onUpdate: paint});
      const drift = scene ? scene.media : hero.querySelector('.lineup-frame');
      const fx = drift && gsap.quickTo(drift, 'x', {duration: 1.4, ease: 'power3.out'});
      const fy = drift && gsap.quickTo(drift, 'y', {duration: 1.4, ease: 'power3.out'});
      hero.addEventListener('pointermove', e => {
        const r = hero.getBoundingClientRect();
        lx(e.clientX - r.left);
        ly(e.clientY - r.top);
        if (drift) {
          fx(((e.clientX - r.left) / r.width - 0.5) * -16);
          fy(((e.clientY - r.top) / r.height - 0.5) * -10);
        }
      });
    } else {
      // Touch: let the light wander slowly on its own.
      gsap.to(light, {x: hero.clientWidth * 0.35, y: hero.clientHeight * 0.65, duration: 6,
        ease: 'sine.inOut', yoyo: true, repeat: -1, onUpdate: paint});
    }
  }

  // Headings rise in word by word as they enter.
  document.querySelectorAll('main h2').forEach(h => {
    if (h.closest('.hero')) return;
    gsap.from(splitWords(h), {
      yPercent: 115, duration: 1.1, ease: 'expo.out', stagger: 0.07,
      scrollTrigger: {trigger: h, start: 'top 88%', once: true}
    });
  });

  // Story: words brighten as you read down the section.
  const story = document.querySelector('.origin-story');
  if (story) {
    const words = [...story.querySelectorAll('p')].flatMap(splitWords);
    gsap.fromTo(words, {opacity: 0.4}, {
      opacity: 1, ease: 'none', stagger: 0.05,
      scrollTrigger: {trigger: story, start: 'top 72%', end: 'bottom 50%', scrub: 0.6}
    });
    const photo = document.querySelector('.origin-photo img');
    if (photo) gsap.fromTo(photo, {clipPath: 'inset(100% 0 0 0)', scale: 1.15}, {
      clipPath: 'inset(0% 0 0 0)', scale: 1, ease: 'none',
      scrollTrigger: {trigger: photo, start: 'top 90%', end: 'center 55%', scrub: 0.8}
    });
  }

  // Product cards: staggered entrance, then the photo drifts toward the pointer under a soft glare.
  // Card photos use mix-blend-mode: multiply, which breaks if the card itself keeps a transform or
  // opacity layer, so the entrance clears its props and the hover moves only the <img>.
  const cards = gsap.utils.toArray('.product-card');
  if (cards.length) {
    gsap.set(cards, {autoAlpha: 0, y: 48});
    ScrollTrigger.batch(cards, {
      start: 'top 90%',
      once: true,
      onEnter: batch => gsap.to(batch, {
        autoAlpha: 1, y: 0, duration: 1.1, ease: 'power3.out', stagger: 0.12, clearProps: 'all'
      })
    });
  }
  if (fine) cards.forEach(card => {
    const img = card.querySelector('.product-image img');
    const glare = document.createElement('div');
    glare.className = 'th-glare';
    card.append(glare);
    const ix = img && gsap.quickTo(img, 'x', {duration: 0.8, ease: 'power3.out'});
    const iy = img && gsap.quickTo(img, 'y', {duration: 0.8, ease: 'power3.out'});
    card.addEventListener('pointermove', e => {
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
      if (img) { ix((x - 0.5) * 12); iy((y - 0.5) * 8); }
      card.style.setProperty('--gx', x * 100 + '%');
      card.style.setProperty('--gy', y * 100 + '%');
    });
    card.addEventListener('pointerleave', () => { if (img) { ix(0); iy(0); } });
  });

  // Cursor: dot + trailing ring that opens up over anything clickable.
  if (fine) {
    const make = cls => {
      const el = document.createElement('div');
      el.className = cls;
      document.body.append(el);
      gsap.set(el, {xPercent: -50, yPercent: -50});
      return el;
    };
    const dot = make('th-cursor-dot'), ring = make('th-cursor-ring');
    const dx = gsap.quickTo(dot, 'x', {duration: 0.1, ease: 'power3.out'});
    const dy = gsap.quickTo(dot, 'y', {duration: 0.1, ease: 'power3.out'});
    const rx = gsap.quickTo(ring, 'x', {duration: 0.55, ease: 'power3.out'});
    const ry = gsap.quickTo(ring, 'y', {duration: 0.55, ease: 'power3.out'});
    root.classList.add('th-has-cursor');
    addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse') return;
      root.classList.add('th-cursor-in');
      dx(e.clientX); dy(e.clientY); rx(e.clientX); ry(e.clientY);
    });
    document.addEventListener('pointerleave', () => root.classList.remove('th-cursor-in'));
    document.addEventListener('pointerover', e => {
      ring.classList.toggle('is-hover',
        !!e.target.closest('a, button, [role="button"], summary, label, select, .product-card, xo-modal-trigger'));
    });
    addEventListener('pointerdown', () => gsap.fromTo(ring, {scale: 0.7}, {scale: 1, duration: 0.6, ease: 'elastic.out(1, 0.45)'}));
  }

  addEventListener('load', () => ScrollTrigger.refresh());
})();
